import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { TFunction } from 'i18next';
import { JsonObject, ProtoUtil } from '@cover42/protobuf-util';
import { cloneDeep } from 'lodash';
import { IPersonalDataStepGM } from './steps/PersonalDataStep';
import {
  IAccountLeadData,
  IBFInsuredObjectDevice,
  IBFInsuredObjectItem,
  IBFProductVariable,
  ILeadData,
  IPolicyData,
  IPolicyObject,
  IPolicyObjectPayload,
  IPolicyVariable,
  ITariffDataStep,
} from 'Services/widgets/interfaces';
import { apiDateTimeFormat, defaultDateFormat } from 'App/ui-utils';
import { IProductDataGM } from './GarantieMax';
import { FIELD_TYPE_DATETIME, FIELD_TYPE_BOOL, FIELD_TYPE_NUMBER, isObject } from '../bf-hooks';
import { InsuredObjectTypes, ProductFieldTypeEntity } from 'Services/widgets/enums';
import { DeviceGroups, DeviceTypes, FamilyTypes, FieldDeviceTypes } from 'Services/widgets/enums';
import { ICFFactorType, ICFFactorTypeOption } from '../factor-service';
import { IDeviceItem } from '../../../../Services/widgets/interfaces';
import { billingNameField } from './steps/tariff-sub-steps/TariffSubStep2';

dayjs.extend( utc );

export const maxCountMobile: number = 4;
export const maxCountDevices: number = 30;

export const deviceTypes: string[] = [ DeviceTypes.Device, DeviceTypes.MobileDevice ];
export const factorTypes: string[] = [ DeviceGroups.DeviceTypeMobile, DeviceGroups.DeviceTypeOther ];
export const extendedCoverage = [
  'extendedCoverageOne',
  'extendedCoverageTwo',
  'extendedCoverageThree',
  'extendedCoverageFour',
];

export const addOnFamilyList = [ FamilyTypes.Family1, FamilyTypes.Family2, FamilyTypes.Family3 ];

export const billingFrom = dayjs().add( 1, 'day' ).utc().format( apiDateTimeFormat );
export const billingTo = dayjs( billingFrom ).add( 1, 'years' ).utc().format( apiDateTimeFormat );

export const prepareDataForTariffGM = (
  data: JsonObject,
  deviceItems: IDeviceItem[],
  insuredObjects: IBFInsuredObjectItem[],
  isEncodeData: boolean = true,
): IPolicyObject[] => {
  const cloneData = cloneDeep( data );
  const cloneInsuredObjects = cloneDeep( insuredObjects );
  const policyObjects: IPolicyObject[] = [];

  deviceItems.forEach( ( deviceItem ) => {
    const insuredObject = cloneInsuredObjects.find( ( insured ) => insured.id === deviceItem.insuredId );

    if ( insuredObject && insuredObject.productFields ) {
      const dataField: JsonObject = {};

      insuredObject.productFields.forEach( ( field ) => {
        const fieldName = field.name;
        const keyName = `${fieldName}_${insuredObject.id}`;
        const itemValue = cloneData[keyName];

        if ( itemValue === undefined ) {
          let defaultValue = field.defaultValue;

          if ( ( field.typeId === FIELD_TYPE_DATETIME && field.isHidden ) || isObject( defaultValue ) ) {
            const objectDate = defaultValue as JsonObject;
            if ( objectDate.hasOwnProperty( 'evaluated' ) ) {
              defaultValue = objectDate.evaluated as string;
            }
          }

          dataField[fieldName] = defaultValue;
        } else {
          if ( isObject( itemValue ) && itemValue['key'] ) {
            dataField[fieldName] = itemValue['key'];
          } else {
            dataField[fieldName] = itemValue;
          }
        }
      } );

      const newPolicyObject = {
        insuredObjectId: insuredObject.id,
        data: isEncodeData ? ProtoUtil.encodeStruct( dataField ) : dataField,
      };

      policyObjects.push( newPolicyObject );
    }
  } );

  return policyObjects;
};

export const preparedDefaultGroupGM = (
  data: JsonObject,
  tariffData: JsonObject,
  personalData: IPersonalDataStepGM,
  deviceItems: IDeviceItem[],
  insuredObjects: IBFInsuredObjectItem[],
  isEncodeData: boolean = true,
): IPolicyObject[] => {
  const cloneData = cloneDeep( data );
  const cloneTariffData = cloneDeep( tariffData );
  const cloneInsuredObjects = cloneDeep( insuredObjects );
  const policyObjects: IPolicyObject[] = [];
  const personalDataFields = [ 'optInNewsletter' ];
  const fieldTerms = 'isAcceptedTermsAndConditions';
  const fieldPrivacyPolicy = 'isAcceptedPrivacyPolicy';
  const brokerID = 'brokerID';
  const fieldAcceptedBrokerInfo = 'isAcceptedBrokerInfo';

  const devices = deviceItems.filter( ( device ) => device.typeDevice === DeviceTypes.MobileDevice );
  const devicesCount = devices.length;
  const defaultGroup = cloneInsuredObjects.find( ( group ) => group.name === InsuredObjectTypes.Default );

  if ( defaultGroup && defaultGroup.productFields ) {
    const dataField: JsonObject = {};

    defaultGroup.productFields.forEach( ( item ) => {
      let defaultValue = item.defaultValue;

      if ( ( item.typeId === FIELD_TYPE_DATETIME && defaultValue ) || isObject( defaultValue ) ) {
        const objectDate = defaultValue as JsonObject;
        if ( objectDate.hasOwnProperty( 'evaluated' ) ) {
          defaultValue = objectDate.evaluated as string;
        }
      }

      if ( item.name === brokerID ) {
        const brokerVal = cloneData[brokerID];
        dataField[item.name] = brokerVal ? brokerVal : defaultValue;
      } else {
        dataField[item.name] = defaultValue;
      }
    } );

    dataField[FamilyTypes.Family1] = '0';
    dataField[FamilyTypes.Family2] = '0';
    dataField[FamilyTypes.Family3] = '0';

    if ( devicesCount === 2 ) {
      dataField[FamilyTypes.Family1] = '1';
    }

    if ( devicesCount === 3 ) {
      dataField[FamilyTypes.Family1] = '1';
      dataField[FamilyTypes.Family2] = '1';
    }

    if ( devicesCount === 4 ) {
      dataField[FamilyTypes.Family1] = '1';
      dataField[FamilyTypes.Family2] = '1';
      dataField[FamilyTypes.Family3] = '1';
    }

    dataField[billingNameField] = cloneData[billingNameField];

    if ( personalData ) {
      personalDataFields.forEach( ( fieldName ) => {
        dataField[fieldName] = personalData[fieldName];
      } );
    }

    if ( cloneData[fieldTerms] ) {
      dataField[fieldTerms] = cloneData[fieldTerms];
    }

    if ( cloneData[fieldPrivacyPolicy] ) {
      dataField[fieldPrivacyPolicy] = cloneData[fieldPrivacyPolicy];
    }

    if ( cloneTariffData[fieldAcceptedBrokerInfo] ) {
      dataField[fieldAcceptedBrokerInfo] = cloneTariffData[fieldAcceptedBrokerInfo];
    }

    if( devices && devices.length ) {
      extendedCoverage.forEach( ( coverageItem, idx ) => {
        const device = devices[idx];
        if ( device ) {
          const coverageVal = cloneTariffData[`extendedCoverage_${device.insuredId!}`];
          dataField[coverageItem] = coverageVal ? coverageVal : '0';
        } else {
          dataField[coverageItem] = '0';
        }
      } );
    }

    const newPolicyObject = {
      insuredObjectId: defaultGroup.id,
      data: isEncodeData ? ProtoUtil.encodeStruct( dataField ) : dataField,
    };

    policyObjects.push( newPolicyObject );
  }

  return policyObjects;
};

export const prepareAccountForInvoice = ( data: JsonObject ): JsonObject => {
  const cloneData = cloneDeep( data );

  Object.keys( cloneData ).forEach( ( fieldName ) => {
    const itemValue = cloneData[fieldName];

    if ( isObject( itemValue ) && itemValue['name'] ) {
      cloneData[fieldName] = itemValue['name'];
    }
  } );

  return cloneData;
};

export const prepareAccountData = ( data: IPersonalDataStepGM ): IAccountLeadData => {
  const newAccountData: IAccountLeadData = {
    gender: 0,
    firstName: '',
    lastName: '',
    zipCode: '',
    city: '',
    street: '',
    houseNumber: '',
    phone: '',
    birthDate: dayjs( new Date() ).format( apiDateTimeFormat ),
    email: '',
  };
  Object.keys( data ).forEach( ( fieldName ) => {
    const itemValue = data[fieldName];

    if ( fieldName === 'birthDate' ) {
      if ( itemValue ) {
        newAccountData[fieldName] = dayjs( itemValue ).format( apiDateTimeFormat );
        return;
      }
    }

    if ( isObject( itemValue ) && itemValue['name'] ) {
      newAccountData[fieldName] = itemValue['name'];
    } else {
      newAccountData[fieldName] = itemValue;
    }

  } );

  delete newAccountData['isAcceptedBrokerInfo'];
  delete newAccountData['optInNewsletter'];

  return newAccountData;
};

const getValueFactorField = (
  nameField: string,
  data: ITariffDataStep,
  productData: IProductDataGM,
  variable: IBFProductVariable,
) => {

  if ( productData === null ) {
    return;
  }

  const selectedVal = data[nameField];

  if ( isObject( selectedVal ) && selectedVal['name'] ) {
    return selectedVal['name'];
  }

  if ( selectedVal ) {
    const findFactor = productData.factors.find( ( f ) => f.id === variable.typeId )!;

    if ( findFactor ) {
      const factorInfo = findFactor?.values.find( ( f ) => f.key === selectedVal )!;
      return factorInfo?.name;
    }
  }

  return '-';
};

const getValueBaseField = (
  nameField: string,
  data: ITariffDataStep,
  variable: IBFProductVariable,
  t: TFunction ) => {
  let selectedVal = data[nameField];

  if ( variable.typeId === FIELD_TYPE_BOOL && selectedVal ) {
    return selectedVal ? t( 'bookingFunnel.tariff.yes' ) : t( 'bookingFunnel.tariff.no' );
  }

  // convert number type to string for correctly view on Django template
  if ( typeof selectedVal === 'number' ) {
    selectedVal = selectedVal.toString();
  }

  if ( variable.typeId === FIELD_TYPE_DATETIME ) {
    selectedVal = dayjs( selectedVal ).startOf( 'day' ).format( defaultDateFormat );
  }

  return selectedVal ? selectedVal : '-';
};

export const prepareValuesForAplication = (
  lead: ILeadData,
  productData: IProductDataGM,
  t: TFunction ): IPolicyData[] => {
  const cloneLead = cloneDeep( lead );
  const deviceItems = cloneLead.subStep1DeviceItems!;
  const policyData: IPolicyData[] = [];
  deviceItems.forEach( ( deviceItem, idx ) => {
    const insuredObject = productData.insuredObjects.find( ( insured ) => insured.id === deviceItem.insuredId );

    if ( insuredObject && insuredObject.productFields ) {
      const variableData = insuredObject.productFields.filter( ( v ) => !v.isHidden );

      if ( variableData.length > 0 ) {
        const listVariables: IPolicyVariable[] = [];
        variableData.forEach( ( field ) => {
          const newItem: IPolicyVariable = {
            label: field.label,
            value: '',
          };

          const tariffData = { ...lead.subStep1TariffData };
          const fieldName = `${field.name}_${insuredObject.id}`;
          if ( field.typeEntity === ProductFieldTypeEntity.SystemProductFieldType ) {
            newItem.value = getValueBaseField( fieldName, tariffData, field, t );
          }

          if ( field.typeEntity === ProductFieldTypeEntity.ProductFactorType ) {
            newItem.value = getValueFactorField( fieldName, tariffData, productData, field );
          }

          listVariables.push( newItem );

        } );

        const newPolicyItem: IPolicyData = {
          id: insuredObject.id!,
          groupName: t( 'bookingFunnel.tariff.deviceTitle', { idx: idx + 1 } ),
          variables: listVariables,
        };

        policyData.push( newPolicyItem );
      }
    }
  } );

  return policyData;
};

export const prepareDefaulGroupAplication = (
  policyObjects: IPolicyObject[],
  productData: IProductDataGM,
  t: TFunction ): IPolicyObjectPayload | null => {
  const defaultGroup = policyObjects[0];
  const cloneData = cloneDeep( defaultGroup.data );
  const insuredObject = productData.insuredObjects.find(
    ( insured ) => insured.id === defaultGroup.insuredObjectId,
  );

  if ( insuredObject && insuredObject.productFields ) {
    const defaultItem: IPolicyObjectPayload = {
      timesliceId: insuredObject.id!,
      insuredObjectName: insuredObject.name,
      insuredObject: insuredObject,
      data: cloneData as JsonObject,
    };

    return defaultItem;
  }

  return null;
};

export const generateVariables = ( insuredObjects: IBFInsuredObjectItem[] ): IBFProductVariable[] => {
  let variableList: IBFProductVariable[] = [];
  insuredObjects.forEach( ( object ) => {

    if ( object.productFields === undefined ) {
      return;
    }

    object.productFields!.forEach( ( item ) => {
      variableList.push( item );
    } );
  } );

  return variableList;
};

export const prepareFormData = ( variables: IBFProductVariable[], formData: ITariffDataStep ): ITariffDataStep => {
  const resForm: ITariffDataStep = cloneDeep( formData );
  const formKeys = Object.keys( resForm );
  formKeys.forEach( ( keyName ) => {
    const variable = variables.find( ( v ) => v.name === keyName );

    if ( variable ) {

      if ( variable.typeId === FIELD_TYPE_BOOL ) {
        resForm[keyName] = Boolean( resForm[keyName] );
      }

      if ( variable.typeId === FIELD_TYPE_NUMBER ) {
        resForm[keyName] = Number( resForm[keyName] );
      }
    }
  } );

  return resForm;
};

export const prepareFormDataGM = (
  deviceItems: IDeviceItem[],
  insuredObjects: IBFInsuredObjectItem[],
  formData: ITariffDataStep,
): ITariffDataStep => {
  const resForm: ITariffDataStep = cloneDeep( formData );

  deviceItems.forEach( ( deviceItem ) => {
    const insuredObject = insuredObjects.find( ( insured ) => insured.id === deviceItem.insuredId );

    if ( insuredObject && insuredObject.productFields ) {
      insuredObject.productFields.forEach( ( field ) => {
        const fieldName = field.name;
        const keyName = `${fieldName}_${insuredObject.id}`;

        if ( field.typeId === FIELD_TYPE_BOOL ) {
          resForm[keyName] = Boolean( resForm[keyName] );
        }

        if ( field.typeId === FIELD_TYPE_NUMBER ) {
          resForm[keyName] = Number( resForm[keyName] );
        }
      } );
    }
  } );

  return resForm;
};

export const selectInsuredObject = (
  insuredObjects: IBFInsuredObjectItem[],
  selectItem: ICFFactorTypeOption,
  insuredObjectIds: ( number | null )[],
): IBFInsuredObjectDevice | null => {
  let insuredObject: IBFInsuredObjectDevice | null = null;
  const { key, productFactorId } = selectItem;

  insuredObjects.sort( ( a, b ) => b.name.localeCompare( a.name ) )
    .forEach( ( insured ) => {
      if ( insured && insured.productFields ) {

        const foundDeviceType = insured.productFields.find(
          ( field ) => field.name === FieldDeviceTypes.DeviceType && field.typeId === productFactorId,
        );

        if ( foundDeviceType && !insuredObjectIds.includes( insured.id ) ) {
          const updateField: IBFProductVariable = {
            ...foundDeviceType,
            defaultValue: key,
          };

          const deviceType = insured.name.replace( /[0-9]/g, '' );

          insuredObject = {
            ...insured,
            deviceType: deviceType as DeviceTypes,
          };

          const idxField = insured.productFields.findIndex( ( field ) => field.id === updateField.id );

          if ( idxField !== -1 && insuredObject.productFields ) {
            insuredObject.productFields[idxField] = updateField;
          }

          return insuredObject;
        }
      }
    } );

  return insuredObject;
};

export const prepareValuesForAplicationGM = (
  lead: ILeadData,
  productData: IProductDataGM,
  t: TFunction ): IPolicyObjectPayload[] => {
  const cloneLead = cloneDeep( lead );
  const deviceItems = cloneLead.subStep1DeviceItems!;
  const policyData: IPolicyObjectPayload[] = [];
  const tariffData = { ...cloneLead.subStep1TariffData };

  deviceItems.forEach( ( deviceItem, idx ) => {
    const insuredObject = productData.insuredObjects.find( ( insured ) => insured.id === deviceItem.insuredId );

    if ( insuredObject && insuredObject.productFields ) {
      const variableData = insuredObject.productFields;

      if ( variableData.length > 0 ) {
        const listVariables: JsonObject = {};
        variableData.forEach( ( field ) => {

          const fieldName = `${field.name}_${insuredObject.id}`;

          listVariables[field.name] = tariffData[fieldName];
        } );

        const newPolicyItem: IPolicyObjectPayload = {
          timesliceId: insuredObject.id!,
          insuredObjectName: insuredObject.name,
          insuredObject: insuredObject,
          data: listVariables,
        };

        policyData.push( newPolicyItem );
      }
    }
  } );

  return policyData;
};

export const getFactorItem = (
  currentItem: ICFFactorTypeOption,
  factors: ICFFactorType[],
): ICFFactorTypeOption | undefined => {
  const cloneFactors: ICFFactorType[] = cloneDeep( factors.filter( ( f ) => factorTypes.includes( f.group ) ) );

  for ( let i = 0; i < cloneFactors.length; i++ ) {
    const factor = cloneFactors[i];
    const foundItem = factor && factor.values.find( ( itemFactor ) => itemFactor.name === currentItem.name );

    if ( foundItem ) {
      foundItem.productFactorId = foundItem.productFactorId ? foundItem.productFactorId : factor.id;

      return foundItem;
    }
  }

  return undefined;
};

export const getInsuredObject = (
  insuredObjects: IBFInsuredObjectItem[],
  selectItem: ICFFactorTypeOption,
  insuredId: number | null,
): IBFInsuredObjectDevice | null => {
  let insuredObject: IBFInsuredObjectDevice | null = null;
  const { key, productFactorId } = selectItem;

  const foundInsured = insuredObjects.find( ( insured ) => insured.id === insuredId );

  if ( foundInsured && foundInsured.productFields ) {

    const foundDeviceType = foundInsured.productFields.find(
      ( field ) => field.name === FieldDeviceTypes.DeviceType && field.typeId === productFactorId,
    );

    if ( foundDeviceType ) {
      const updateField: IBFProductVariable = {
        ...foundDeviceType,
        defaultValue: key,
        isHidden: true,
      };

      const deviceType = foundInsured.name.replace( /[0-9]/g, '' );

      insuredObject = {
        ...foundInsured,
        deviceType: deviceType as DeviceTypes,
      };

      const idxField = foundInsured.productFields.findIndex( ( field ) => field.id === updateField.id );

      if ( idxField !== -1 && insuredObject.productFields ) {
        insuredObject.productFields[idxField] = updateField;
      }

      return insuredObject;
    }
  }

  return insuredObject;
};

export const clearAddOnFamilyFields = (
  tariffFormData: ITariffDataStep,
  insuredObjects: IBFInsuredObjectItem[],
): ITariffDataStep => {
  const cloneTariffFormData = cloneDeep( tariffFormData );
  const defaultGroup = insuredObjects.find( ( group ) => group.name === InsuredObjectTypes.Default );

  // Clear data addOnFamily in storage
  for ( let i = 0; i < addOnFamilyList.length; i++ ) {
    const familyItem = addOnFamilyList[i];
    const fieldName = `${familyItem}_${defaultGroup?.id}`;
    if ( cloneTariffFormData && cloneTariffFormData[fieldName] ) {
      delete cloneTariffFormData[fieldName];
    }
  }

  return cloneTariffFormData;
};

export const updatedAddOnFamilyItems = ( items: IDeviceItem[] ): IDeviceItem[] => {
  const mobileDevice = items.filter( ( device ) => device.typeDevice === DeviceTypes.MobileDevice );
  if ( !mobileDevice ) {
    return items;
  }

  for ( let i = 0; i < mobileDevice.length; i++ ) {
    const device = mobileDevice[i];
    const idxDevice = items.findIndex( ( object ) => object.insuredId === device.insuredId );
    if ( idxDevice !== -1 ) {
      const foundDevice = items[idxDevice];
      let newItem: IDeviceItem ={
        ...foundDevice,
        addOnFamily: false,
        showFields: true,
      };

      if ( i >= 1 ) {
        newItem.addOnFamily = true;
        newItem.showFields = true;
      }

      items[idxDevice] = newItem;
    }
  }

  return items;
};

export const sortInsuredObjects = ( insuredObjects: IBFInsuredObjectItem[] ): IBFInsuredObjectItem[] => {
  const mobileType = DeviceTypes.MobileDevice;
  const strMobileLength = mobileType.length;
  const diviceType = DeviceTypes.Device;
  const strDeviceLength = diviceType.length;

  return insuredObjects.map( ( insured ) => {
    if ( insured.productFields ) {
      const typeMobileInsured = insured.name.substring( 0, strMobileLength );
      const typeDeviceInsured = insured.name.substring( 0, strDeviceLength );

      if ( typeMobileInsured === mobileType ) {
        const idxFieldMobile = {
          deviceDetails: 1,
          extendedCoverage: 2,
          purchaseDate: 3,
          purchasePrice: 4,
          deviceId: 5,
          deviceType: 6,
          purchaseInvoicePath: 7,
        };

        insured.productFields.sort( ( a, b ) => idxFieldMobile[a.name] - idxFieldMobile[b.name] );
      }

      if ( typeDeviceInsured === diviceType ) {
        const idxFieldDevice = {
          deviceDetails: 1,
          purchaseDate: 2,
          purchasePrice: 3,
          deviceId: 4,
          deviceType: 5,
          purchaseInvoicePath: 6,
        };

        insured.productFields.sort( ( a, b ) => idxFieldDevice[a.name] - idxFieldDevice[b.name] );
      }

      insured.productFields.map( ( item ) => {
        if ( deviceTypes.includes( item.name ) ) {
          item.isHidden = true;
        }

        return item;
      } );
    }

    return insured;
  } );
};
